import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAudit } from "./AuditContext";
import { useIsMobileAudit } from "./useIsMobileAudit";

/**
 * Quick UI Tour — 5-step spotlight onboarding played once, right after the
 * welcome dialogue and before the Room 01 task. The presenter (guide-point
 * pose) points at each control zone while the rest of the room dims.
 * Fully interactive: each step waits for a click/tap — nothing auto-advances.
 */

const TOUR_STEPS = [
  {
    id: "correction",
    text: "If you need to step back or clear your choices in a room, use these tools here.",
    spotlight: { left: 12, bottom: 12, width: 330, height: 76, borderRadius: 14 },
    callout: { left: 28, bottom: 120 },
    pointLeft: true,
  },
  {
    id: "replay",
    text: "Missed something? Tap here to replay my instructions at any time.",
    spotlight: { left: 10, top: 10, width: 64, height: 64, borderRadius: 999 },
    callout: { left: 92, top: 34 },
    pointLeft: true,
  },
  {
    id: "system",
    text: "You can exit or restart the entire audit from this corner.",
    spotlight: { right: 12, top: 76, width: 128, height: 62, borderRadius: 14 },
    callout: { right: 160, top: 70 },
    pointLeft: false,
  },
  {
    id: "progress",
    text: "This is your stage progress map — it tracks your journey through all seven rooms. Gold marks the rooms you\u2019ve completed; tap one to revisit it any time.",
    spotlight: { left: "50%", top: 6, width: 230, height: 48, borderRadius: 12, marginLeft: -115 },
    callout: { left: "7%", top: 66 },
    pointLeft: false,
  },
  {
    id: "action",
    text: "And when you\u2019re ready to move forward, this is your way out. Let\u2019s start.",
    spotlight: { right: 14, bottom: 14, width: 250, height: 78, borderRadius: 14 },
    callout: { right: 34, bottom: 128 },
    pointLeft: false,
  },
];

export function UITour() {
  const { tourActive, completeTour, selectedPresenter } = useAudit();
  const [step, setStep] = useState(0);
  const isMobile = useIsMobileAudit();

  useEffect(() => {
    if (tourActive) setStep(0);
  }, [tourActive]);

  if (!tourActive) return null;

  const s = TOUR_STEPS[step];
  const advance = () =>
    step >= TOUR_STEPS.length - 1 ? completeTour() : setStep(step + 1);

  const presenterSrc = `${selectedPresenter.avatarBase}/${selectedPresenter.poses.guidePoint}`;

  // Mobile-only: the small "guide" presenter that floats next to each
  // callout bubble is dropped — the room's own main presenter (already
  // visible behind the dimmed overlay) is enough of an anchor, and on a
  // narrow phone screen this second figure was crowding the bubble text.
  const miniPresenter = isMobile ? null : (
    <img
      src={presenterSrc}
      alt=""
      draggable={false}
      className="select-none block shrink-0"
      style={{
        height: 210,
        // guide-point naturally points right — flip when the zone is on the left
        transform: `scaleX(${s.pointLeft ? -1 : 1})`,
        filter: "drop-shadow(0 10px 24px rgba(0,0,0,0.5))",
      }}
    />
  );

  return (
    <div
      className="absolute inset-0 z-[120] cursor-pointer"
      onClick={advance}
      data-testid="audit-ui-tour"
    >
      {/* Spotlight — transparent hole; the giant box-shadow dims the room */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`spot-${s.id}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="absolute pointer-events-none"
          style={{
            ...s.spotlight,
            boxShadow:
              "0 0 0 9999px rgba(8,6,3,0.78), 0 0 42px rgba(218,152,60,0.45)",
            border: "2px solid #da983c",
          }}
          data-testid={`tour-spotlight-${s.id}`}
        />
      </AnimatePresence>

      {/* Callout — presenter + bubble pointing at the zone */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`callout-${s.id}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="absolute flex items-end gap-3 pointer-events-none"
          style={{ ...s.callout, maxWidth: 620 }}
          data-testid={`tour-callout-${s.id}`}
        >
          {!s.pointLeft && miniPresenter}
          <div
            className="bg-white text-[#1B1B18] px-6 py-4 shadow-[0_18px_44px_rgba(0,0,0,0.5)]"
            style={{ borderRadius: 22, marginBottom: 36 }}
          >
            <p className="font-serif text-lg leading-snug">{s.text}</p>
          </div>
          {s.pointLeft && miniPresenter}
        </motion.div>
      </AnimatePresence>

      {/* Progress + tap hint + skip */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-paper/80 animate-pulse pointer-events-none">
          tap anywhere to continue
        </p>
        <div className="flex items-center gap-2" data-testid="tour-progress">
          {TOUR_STEPS.map((_, i) => (
            <span
              key={i}
              className="block rounded-full transition-colors duration-300"
              style={{
                width: 8,
                height: 8,
                background: i === step ? "#da983c" : "rgba(255,255,255,0.35)",
              }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            completeTour();
          }}
          data-testid="tour-skip"
          className="pointer-events-auto font-mono text-[10px] uppercase tracking-[0.24em] px-6 border border-paper/40 bg-black/40 text-paper/85 hover:border-paper hover:text-paper transition-colors backdrop-blur-sm"
          style={{ minHeight: 44 }}
        >
          Skip Tour
        </button>
      </div>
    </div>
  );
}

export default UITour;
